import React, { useState } from "react";
import {
  Box, Flex, Text, Button, Badge, HStack, Stack, Divider, Alert, AlertIcon,
  Table, Thead, Tbody, Tr, Th, Td, Tooltip, SimpleGrid,
} from "@chakra-ui/react";
import {
  AlertTriangle, CheckCircle, Clock, HelpCircle, XCircle, ReceiptText,
  RefreshCw, ShieldCheck, Download, Ban, Pencil,
} from "lucide-react";
import {
  confirmarNotaClienteDevolucao, descartarNotasClienteDevolucao,
  baixarNotaClienteDevolucao, salvarMarcacoesNotaDevolucao,
} from "../../services/api";
import { formatBRL, formatDate } from "../../utils/formatters";
import MarcacoesNotaDevolucao, {
  TIPOS_NOTA_DEVOLUCAO, marcacoesIniciais, errosMarcacoes, payloadMarcacoes,
} from "../MarcacoesNotaDevolucao";

const ACCENT = "#F68633";

const STATUS = {
  pendente: {
    label: "Na fila", cor: "gray", icone: Clock,
    ajuda: "A nota sai alguns minutos depois do recebimento. Esse intervalo existe para dar tempo de desfazer.",
  },
  aguardando_confirmacao: {
    label: "Aguardando confirmação", cor: "orange", icone: HelpCircle,
    ajuda: "O cliente é CNPJ e encontramos uma nota de devolução emitida por ele. Confirme se é essa antes de emitirmos a nossa.",
  },
  emitida: {
    label: "Emitida", cor: "green", icone: CheckCircle,
    ajuda: "NF-e de devolução autorizada pela SEFAZ.",
  },
  dispensada_nota_cliente: {
    label: "Nota do cliente", cor: "blue", icone: ReceiptText,
    ajuda: "O cliente emitiu a nota de devolução dele. A TAGA não emitiu outra para não abater o imposto em dobro.",
  },
  erro: {
    label: "Erro", cor: "red", icone: AlertTriangle,
    ajuda: "A nota não foi emitida. Resolva o motivo abaixo e tente de novo.",
  },
  cancelada: {
    label: "Cancelada", cor: "gray", icone: XCircle,
    ajuda: "O recebimento foi desfeito antes da emissão. Nenhuma nota foi emitida.",
  },
  bloqueada: {
    label: "Bloqueada", cor: "yellow", icone: Ban,
    ajuda: "Uma regra fiscal impediu a emissão. Corrija o motivo abaixo e emita de novo.",
  },
};

/**
 * Ambiente em que a nota saiu. Nota de homologacao e nota valendo aparecem as
 * duas como "Emitida": sem este selo, ninguem distingue ensaio de documento
 * fiscal na hora de mandar para o contador.
 */
export const seloAmbienteNota = (ambiente, curto = false) => {
  if (!ambiente) return null;
  const producao = ambiente === "producao";
  return (
    <Badge colorScheme={producao ? "green" : "purple"} variant="subtle"
      fontSize="10px" px={2} borderRadius="md" whiteSpace="nowrap">
      {producao ? (curto ? "PROD" : "PRODUÇÃO") : (curto ? "HMG" : "HOMOLOGAÇÃO")}
    </Badge>
  );
};

const ORIGEM_CANDIDATO = {
  refnfe: { label: "Prova", cor: "green" },
  itens: { label: "Forte", cor: "orange" },
  resumo: { label: "Fraco", cor: "gray" },
};

/**
 * Bloco da NF-e de devolução do recebimento (#2225).
 *
 * Vive em dois lugares com o mesmo estado: na tela /nfe-devolucao-cliente e no card
 * da devolução em /devolucoes. A confirmação da nota do cliente CNPJ é a única
 * ação que exige gente, e é ela que segura a emissão.
 */
export default function PainelNotaDevolucao({ dados, onAtualizado, onReprocessar, toast, compacto = false }) {
  const [acaoEmCurso, setAcaoEmCurso] = useState("");
  const [editando, setEditando] = useState(false);
  const [marcacoes, setMarcacoes] = useState(null);
  const [mostrarErros, setMostrarErros] = useState(false);

  const registro = dados?.registro;
  if (!registro) return null;

  const status = STATUS[registro.status] || { label: registro.status, cor: "gray", icone: Clock, ajuda: "" };
  // Nota de ensaio nao resolve a devolucao: continua faltando a de verdade.
  const ehEnsaio = registro.status === "emitida" && registro.ambiente === "homologacao";
  const Icone = status.icone;
  const candidatos = registro.candidatos || [];

  const confirmar = async (chave) => {
    setAcaoEmCurso(chave);
    const res = await confirmarNotaClienteDevolucao(registro.id, chave);
    setAcaoEmCurso("");

    if (res?.error) {
      toast?.({ title: "Não foi possível confirmar", description: res.error, status: "error", duration: 6000 });
      return;
    }

    toast?.({ title: "Nota do cliente vinculada", description: "A TAGA não vai emitir nota para esta devolução.", status: "success", duration: 5000 });
    onAtualizado?.(res);
  };

  const descartar = async () => {
    setAcaoEmCurso("descartar");
    const res = await descartarNotasClienteDevolucao(registro.id);
    setAcaoEmCurso("");

    if (res?.error) {
      toast?.({ title: "Não foi possível emitir", description: res.error, status: "error", duration: 6000 });
      return;
    }

    toast?.({ title: "Emissão disparada", status: "success", duration: 4000 });
    onAtualizado?.(res);
  };

  const editavel = ["pendente", "erro", "bloqueada"].includes(registro.status)
    || (registro.status === "emitida" && registro.ambiente === "homologacao");

  const abrirEdicao = () => {
    setMarcacoes(marcacoesIniciais(registro));
    setMostrarErros(false);
    setEditando(true);
  };

  const salvarMarcacoes = async () => {
    const erros = errosMarcacoes(marcacoes);
    if (Object.keys(erros).length) {
      setMostrarErros(true);
      return;
    }
    setAcaoEmCurso("marcacoes");
    const res = await salvarMarcacoesNotaDevolucao(registro.id, payloadMarcacoes(marcacoes));
    setAcaoEmCurso("");

    if (res?.error) {
      toast?.({ title: "Não foi possível salvar", description: res.error, status: "error", duration: 6000 });
      return;
    }

    toast?.({ title: "Marcações salvas", description: "Emita de novo para a nota sair com elas.", status: "success", duration: 5000 });
    setEditando(false);
    onAtualizado?.(res);
  };

  const baixarNotaCliente = async (formato) => {
    setAcaoEmCurso(formato);
    const res = await baixarNotaClienteDevolucao(registro.id, formato);
    setAcaoEmCurso("");

    if (res?.error) {
      toast?.({ title: "Não foi possível baixar", description: res.error, status: "error", duration: 7000 });
    }
  };

  return (
    <Box bg="white" borderWidth="1px" borderRadius="lg" p={4}
      borderColor={registro.status === "aguardando_confirmacao" ? "orange.300" : "gray.200"}>
      <Flex justify="space-between" align="flex-start" gap={3} wrap="wrap">
        <HStack spacing={3} align="flex-start">
          <Box color={`${status.cor}.500`} mt="2px"><Icone size={18} /></Box>
          <Box>
            <HStack spacing={2}>
              <Text fontSize="sm" fontWeight="semibold">Nota fiscal de devolução</Text>
              <Badge colorScheme={status.cor} fontSize="10px" px={2} borderRadius="md">{status.label}</Badge>
              {registro.status === "emitida" && seloAmbienteNota(registro.ambiente)}
            </HStack>
            <Text fontSize="xs" color="gray.500" mt={1} maxW="440px">
              {registro.status === "emitida" && registro.ambiente === "homologacao"
                ? "Ensaio na SEFAZ de homologação: não tem valor fiscal. A devolução segue sem a nota de verdade, e emitir de novo substitui esta."
                : status.ajuda}
            </Text>
          </Box>
        </HStack>

        {/* Também em `pendente`: o job roda com tries=1, então worker reiniciado
            deixa o registro parado e alguém precisa poder reenfileirar. */}
        {(["erro", "pendente", "bloqueada"].includes(registro.status) || ehEnsaio) && onReprocessar && (
          <Button size="xs" variant="outline"
            colorScheme={registro.status === "erro" ? "red" : registro.status === "bloqueada" || ehEnsaio ? "orange" : "gray"}
            leftIcon={<RefreshCw size={13} />}
            onClick={onReprocessar}>
            {registro.status === "erro" ? "Tentar de novo" : registro.status === "bloqueada" || ehEnsaio ? "Emitir de novo" : "Emitir agora"}
          </Button>
        )}
      </Flex>

      {registro.status === "emitida" && (
        <>
          <Divider my={3} />
          <HStack spacing={5} fontSize="xs" wrap="wrap">
            <Box>
              <Text color="gray.500" fontSize="10px" textTransform="uppercase">NF-e</Text>
              <Text fontWeight="semibold">{registro.nfe_numero || "—"}</Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="10px" textTransform="uppercase">Valor</Text>
              <Text>{formatBRL(registro.totais?.vNF || 0)}</Text>
            </Box>
            <Box flex="1" minW="200px">
              <Text color="gray.500" fontSize="10px" textTransform="uppercase">Chave</Text>
              <Text wordBreak="break-all">{registro.nfe_chave || "—"}</Text>
            </Box>
          </HStack>
        </>
      )}

      {registro.status === "dispensada_nota_cliente" && (
        <>
          <Divider my={3} />
          <HStack spacing={5} fontSize="xs" wrap="wrap">
            <Box>
              <Text color="gray.500" fontSize="10px" textTransform="uppercase">Nota do cliente</Text>
              <Text fontWeight="semibold">{registro.nota_cliente_numero || "—"}</Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="10px" textTransform="uppercase">Emitida em</Text>
              <Text>{formatDate(registro.nota_cliente_emissao)}</Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="10px" textTransform="uppercase">Valor</Text>
              <Text>{formatBRL(registro.nota_cliente_valor || 0)}</Text>
            </Box>
            <Box flex="1" minW="200px">
              <Text color="gray.500" fontSize="10px" textTransform="uppercase">Emitente</Text>
              <Text noOfLines={1}>{registro.nota_cliente_emitente || "—"}</Text>
            </Box>
          </HStack>

          {/* O contador arquiva a nota do cliente no lugar da nossa, entao ela
              precisa ser baixavel aqui. Nao aparece no painel do faturador:
              nota de entrada nao e nota nossa. */}
          <HStack mt={3} spacing={2}>
            <Button size="xs" variant="outline" leftIcon={<Download size={13} />}
              isLoading={acaoEmCurso === "xml"} onClick={() => baixarNotaCliente("xml")}>
              XML
            </Button>
            <Button size="xs" variant="outline" leftIcon={<Download size={13} />}
              isLoading={acaoEmCurso === "danfe"} onClick={() => baixarNotaCliente("danfe")}>
              DANFE
            </Button>
          </HStack>
        </>
      )}

      {["erro", "bloqueada"].includes(registro.status) && registro.erro && (
        <Alert status={registro.status === "erro" ? "error" : "warning"} borderRadius="md" mt={3} fontSize="xs" alignItems="flex-start">
          <AlertIcon boxSize="14px" mt="2px" />
          <Text>{registro.erro}</Text>
        </Alert>
      )}

      <Divider my={3} />
      {editando ? (
        <Box>
          <MarcacoesNotaDevolucao valores={marcacoes} onChange={setMarcacoes}
            erros={errosMarcacoes(marcacoes)} mostrarErros={mostrarErros} />
          <Flex justify="flex-end" gap={2} mt={3} direction={{ base: "column-reverse", sm: "row" }}>
            <Button size="sm" variant="ghost" onClick={() => setEditando(false)}>Cancelar</Button>
            <Button size="sm" bg={ACCENT} color="white" _hover={{ bg: "#e07529" }}
              isLoading={acaoEmCurso === "marcacoes"} onClick={salvarMarcacoes}>
              Salvar marcações
            </Button>
          </Flex>
        </Box>
      ) : (
        <Flex justify="space-between" align="flex-start" gap={3} wrap="wrap">
          <SimpleGrid columns={{ base: 2, sm: 4 }} spacing={3} fontSize="xs" flex="1" minW="240px">
            <Box>
              <Text color="gray.500" fontSize="10px" textTransform="uppercase">Tipo</Text>
              <Text>{TIPOS_NOTA_DEVOLUCAO[registro.tipo] || TIPOS_NOTA_DEVOLUCAO.devolucao}</Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="10px" textTransform="uppercase">Recebimento</Text>
              <Text>{registro.data_recebimento ? formatDate(registro.data_recebimento) : "-"}</Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="10px" textTransform="uppercase">Garantia</Text>
              <Text>{registro.garantia ? "Sim" : "Não"}</Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="10px" textTransform="uppercase">Cliente MEI</Text>
              <Text>{registro.cliente_mei ? `Sim (${formatDate(registro.mei_consultado_em)})` : "Não"}</Text>
            </Box>
          </SimpleGrid>
          {editavel && (
            <Button size="xs" variant="ghost" leftIcon={<Pencil size={13} />} onClick={abrirEdicao}>
              Editar
            </Button>
          )}
        </Flex>
      )}

      {registro.status === "aguardando_confirmacao" && (
        <>
          <Divider my={3} />
          <Text fontSize="xs" color="gray.600" mb={2}>
            O que voltou: {(dados?.itens || []).map((i) => `${i.codigo} (${Number(i.quantidade)})`).join(", ") || "—"}
            {" · "}{formatBRL(registro.totais?.vProd || 0)}
          </Text>

          {/* Desktop: tabela. A ação é a última coluna e cabe na largura. */}
          <Box display={{ base: "none", md: "block" }} borderWidth="1px" borderRadius="md" overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th fontSize="10px">Nota do cliente</Th>
                  <Th fontSize="10px">Emitida</Th>
                  <Th fontSize="10px" isNumeric>Valor</Th>
                  <Th fontSize="10px">Confiança</Th>
                  <Th fontSize="10px" />
                </Tr>
              </Thead>
              <Tbody>
                {candidatos.map((c) => {
                  const origem = ORIGEM_CANDIDATO[c.origem] || { label: c.origem, cor: "gray" };
                  return (
                    <Tr key={c.chave}>
                      <Td fontSize="xs">
                        <Text fontWeight="semibold">Nº {c.numero || "—"}</Text>
                        <Text color="gray.500" noOfLines={1} maxW="230px">{c.emitente}</Text>
                      </Td>
                      <Td fontSize="xs">{formatDate(c.emissao)}</Td>
                      <Td fontSize="xs" isNumeric>{formatBRL(c.valor)}</Td>
                      <Td>
                        <Tooltip label={c.motivo} hasArrow>
                          <Badge colorScheme={origem.cor} fontSize="10px">{origem.label}</Badge>
                        </Tooltip>
                      </Td>
                      <Td>
                        <Button size="xs" bg={ACCENT} color="white" _hover={{ bg: "#e07529" }}
                          leftIcon={<ShieldCheck size={13} />}
                          isLoading={acaoEmCurso === c.chave}
                          onClick={() => confirmar(c.chave)}>
                          É esta
                        </Button>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>

          {/* Mobile: cartões. Numa tabela o botão "É esta" cai fora da tela, e
              ele é a razão de o bloco existir. */}
          <Stack display={{ base: "flex", md: "none" }} spacing={2}>
            {candidatos.map((c) => {
              const origem = ORIGEM_CANDIDATO[c.origem] || { label: c.origem, cor: "gray" };
              return (
                <Box key={c.chave} borderWidth="1px" borderRadius="md" p={3}>
                  <Flex justify="space-between" align="flex-start" gap={2}>
                    <Box minW={0}>
                      <Text fontSize="sm" fontWeight="semibold">Nº {c.numero || "—"}</Text>
                      <Text fontSize="xs" color="gray.500" noOfLines={2}>{c.emitente}</Text>
                    </Box>
                    <Badge colorScheme={origem.cor} fontSize="10px" flexShrink={0}>{origem.label}</Badge>
                  </Flex>
                  <HStack spacing={4} mt={2} fontSize="xs" color="gray.600">
                    <Text>{formatDate(c.emissao)}</Text>
                    <Text fontWeight="semibold">{formatBRL(c.valor)}</Text>
                  </HStack>
                  <Text fontSize="11px" color="gray.500" mt={1}>{c.motivo}</Text>
                  <Button mt={3} w="100%" size="sm" bg={ACCENT} color="white" _hover={{ bg: "#e07529" }}
                    leftIcon={<ShieldCheck size={14} />}
                    isLoading={acaoEmCurso === c.chave}
                    onClick={() => confirmar(c.chave)}>
                    É esta
                  </Button>
                </Box>
              );
            })}
          </Stack>

          <Flex justify={{ base: "stretch", md: "flex-end" }} mt={3}>
            <Button size="sm" variant="outline" w={{ base: "100%", md: "auto" }}
              isLoading={acaoEmCurso === "descartar"}
              onClick={descartar}>
              Nenhuma serve, emitir a nossa
            </Button>
          </Flex>
        </>
      )}

      {!compacto && registro.status === "pendente" && (
        <Text fontSize="xs" color="gray.400" mt={3}>
          Marcada como recebida em {formatDate(registro.created_at)}.
        </Text>
      )}
    </Box>
  );
}
